/**
 *  @swagger
 *  definitions:
 *    RegattaProtestEndParams:
 *      type: object
 *      properties:
 *        EndDate:
 *          description: End Date of the Protest End
 *          type: string
 *          format: date-time
 *
 *  @swagger
 *  definitions:
 *    RegattaProtestEndCreateParams:
 *      allOf:
 *        - $ref: '#/definitions/RegattaProtestEndParams'
 *        - type: object
 *          required:
 *            - RegattaDayId
 *            - RegattaResultTableId
 *            - EndDate
 *
 *  @swagger
 *  definitions:
 *    RegattaProtestEndQueryParams:
 *      allOf:
 *        - type: object
 *          properties:
 *            id:
 *              description: ID of the RegattaProtestEnd
 *              type: string
 *        - $ref: '#/definitions/RegattaProtestEndParams'
 *
 *  @swagger
 *  definitions:
 *    RegattaProtestEndUpdateParams:
 *      allOf:
 *        - $ref: '#/definitions/RegattaProtestEndQueryParams'
 *        - type: object
 *          required:
 *            - id
 *
 *  @swagger
 *  definitions:
 *    RegattaProtestEnd:
 *      allOf:
 *        - $ref: '#/definitions/RegattaProtestEndCreateParams'
 *        - $ref: '#/definitions/RegattaProtestEndUpdateParams'
 *        - $ref: '#/definitions/Timestamps'
 *
 *  @swagger
 *  components:
 *    schemas:
 *      RegattaProtestEnd:
 *        allOf:
 *          - $ref: '#/definitions/RegattaProtestEnd'
 *          - type: object
 *            properties:
 *              RegattaDay:
 *                description: RegattaDay of the RegattaProtestEnd
 *                $ref: '#/components/schemas/RegattaDay'
 *              RegattaResultTable:
 *                description: RegattaResultTable of the RegattaProtestEnd
 *                $ref: '#/components/schemas/RegattaResultTable'
 */

// Types
import type {
  $Model,
  $ModelClass,
  $ModelClasses,
} from '.';
import type {
  $Field,
} from '../types';

export type $RelationIds = {
  RegattaDayId: string;
  RegattaResultTableId: string;
};

export type $Params = {
  EndDate: Date;
};

export type $CreateParams = $Params & $RelationIds;

export type $UpdateParams = {
  id: string;
} & Partial<$CreateParams>;

export type $UpsertParams = $CreateParams & {
  id?: string;
};

export type $QueryParams = {
  id?: null | string;
} & Partial<$CreateParams>;

export type $Associations = {
  RegattaDay?: $Model<'RegattaDay'>;
  RegattaResultTable?: $Model<'RegattaResultTable'>;
};

export type $ModelParams = $Associations & $CreateParams & {
  id: string;
};

type $Fields = $Params & {
  id: string;
};

const fields = (
  Sequelize,
): Record<keyof $Fields, $Field> => ({
  EndDate: {
    allowNull: false,
    type: Sequelize.DATE,
    validate: {
      notEmpty: true,
    },
  },
  id: {
    allowNull: false,
    defaultValue: Sequelize.UUIDV4,
    primaryKey: true,
    type: Sequelize.UUID,
    unique: true,
  },
});

export default ((
  Sequelize,
  stack,
): $ModelClass<'RegattaProtestEnd'> => {
  const RegattaProtestEnd = stack.define(
    'RegattaProtestEnd',
    fields(Sequelize),
    {
      tableName: 'RegattaProtestEnds',
    },
  );

  RegattaProtestEnd.associate = (models: $ModelClasses) => {
    models.RegattaProtestEnd.belongsTo(
      models.RegattaDay,
      {
        as: 'RegattaDay',
        foreignKey: 'RegattaDayId',
      },
    );
    models.RegattaProtestEnd.belongsTo(
      models.RegattaResultTable,
      {
        as: 'RegattaResultTable',
        foreignKey: 'RegattaResultTableId',
      },
    );
  };

  RegattaProtestEnd.addScope(
    'defaultScope',
    {
      order: [['createdAt', 'ASC']],
    },
  );

  return RegattaProtestEnd;
});
